import { Phone, MessageCircle, MapPin, ShieldCheck, SearchCheck, Wrench, Clock3 } from "lucide-react";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import ServiceIcon, { SERVICE_ICON_BY_SLUG } from "@/components/ServiceIcon";
import DispatchComposer from "@/components/DispatchComposer";
import { useLang } from "@/context/LanguageContext";
import { useData } from "@/context/DataContext";
import Logo from "@/components/Logo";
import { PHONE_TEL, openWa } from "@/lib/wa";

const VAN = "https://customer-assets-v7afamib.emergentagent.net/job_mero-tires/artifacts/2016f23ae4acf2e7_IMG_5488.jpeg";

export default function Landing() {
  const { pick } = useLang();
  const { services } = useData();
  const quick = services.filter((s) => ["tire-change", "puncture-repair", "tpms", "balancing", "batteries"].includes(s.slug));

  return (
    <main>
      <SEO
        title="بنشر متنقل في الرياض يصلك أينما كنت | ميرو للإطارات وخدمات السيارات"
        description="خدمة بنشر متنقل في جميع أحياء الرياض: تغيير كفرات، إصلاح بنشر، برمجة حساسات، ترصيص وتغيير بطاريات — أرسل موقعك عبر واتساب 0507359999 وسنأتي إليك."
        path="/lp"
      />

      <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-ink/85 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5 lg:px-8">
          <a href="/"><Logo /></a>
          <a href={PHONE_TEL} className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm font-bold text-white transition-colors hover:border-gold/60 hover:text-gold" data-testid="lp-header-call" dir="ltr">
            <Phone size={15} /> 0507359999
          </a>
        </div>
      </header>

      {/* HERO */}
      <section className="relative flex min-h-[92vh] items-center overflow-hidden pt-24">
        <img src={VAN} alt="سيارة بنشر متنقل ميرو في الرياض" className="absolute inset-0 h-full w-full object-cover object-[22%_center] opacity-45" data-testid="lp-van-bg" />
        <div className="absolute inset-0 bg-gradient-to-l from-ink via-ink/85 to-ink/40" />
        <div className="relative mx-auto w-full max-w-6xl px-5 py-16 lg:px-8">
          <Reveal>
            <p className="eyebrow mb-5">MERO MOBILE SERVICE — RIYADH</p>
            <h1 className="max-w-2xl text-4xl font-extrabold leading-[1.35] text-white sm:text-5xl lg:text-6xl" data-testid="lp-hero-title">
              بنشر متنقل <span className="gold-text">يصلك أينما كنت</span> في الرياض
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-9 text-zinc-300">
              تغيير كفرات، إصلاح بنشر، برمجة حساسات، ترصيص وتغيير بطاريات — في بيتك أو عملك أو موقع سيارتك. أرسل موقعك الآن وسنتأكد من التغطية والتكلفة قبل التنفيذ.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <button onClick={() => openWa("السلام عليكم، أرغب في طلب بنشر متنقل من ميرو.\nالحي: ____.\nنوع السيارة: ____.\nوصف المشكلة: ____.\nأرجو توضيح التوفر والتكلفة.")} className="btn-wa !px-10 !py-5 !text-lg animate-pulse-gold" data-testid="lp-wa-main-cta">
                <MessageCircle size={22} /> اطلب الآن عبر واتساب
              </button>
              <a href={PHONE_TEL} className="btn-gold !px-8 !py-4" data-testid="lp-call-main-cta"><Phone size={19} /> 0507359999</a>
            </div>
            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-zinc-300">
              {[
                { icon: MessageCircle, ar: "واتساب مباشر دون تسجيل" },
                { icon: ShieldCheck, ar: "تأكيد التغطية والتكلفة قبل التنفيذ" },
                { icon: SearchCheck, ar: "فحص قبل أي عمل" },
                { icon: Clock3, ar: "موعد يناسبك حسب التوفر" },
              ].map((c, i) => (
                <span key={i} className="flex items-center gap-2"><c.icon size={16} className="text-gold" /> {c.ar}</span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* STEPS */}
      <section className="border-t border-white/5 bg-coal/50 py-16">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <Reveal>
            <h2 className="mb-12 text-center text-2xl font-bold text-white sm:text-3xl">كيف تطلب الخدمة؟ <span className="gold-text">3 خطوات فقط</span></h2>
          </Reveal>
          <div className="grid gap-6 md:grid-cols-3">
            {[
              { icon: MessageCircle, ar: "راسلنا واتساب", txt: "اضغط الزر وسيُفتح واتساب برسالة جاهزة — أكمل بياناتك وأرسل." },
              { icon: MapPin, ar: "أرسل موقعك وحيّك", txt: "مشاركة الموقع عبر واتساب هي الأدق، ونتحقق فوراً من التغطية في منطقتك." },
              { icon: Wrench, ar: "يصلك الفني", txt: "نرتب معك الموعد حسب التوفر، ويفحص الفني الإطار ويشرح الوضع قبل أي عمل." },
            ].map((s, i) => (
              <Reveal key={i} delay={i}>
                <div className="relative h-full rounded-3xl border border-white/10 bg-ink/70 p-8 text-center" data-testid={`lp-step-${i + 1}`}>
                  <span className="absolute -top-4 start-1/2 flex h-9 w-9 -translate-x-1/2 items-center justify-center rounded-full bg-gold font-display text-base font-extrabold text-ink">{i + 1}</span>
                  <span className="mx-auto mb-5 mt-2 flex h-14 w-14 items-center justify-center rounded-2xl bg-gold/10 text-gold"><s.icon size={26} /></span>
                  <h3 className="text-lg font-bold text-white">{s.ar}</h3>
                  <p className="mt-3 text-sm leading-7 text-mist">{s.txt}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* COMPOSER */}
      <section className="py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <Reveal>
            <h2 className="mb-3 text-center text-2xl font-bold text-white sm:text-3xl">اطلب في أقل من دقيقة</h2>
            <p className="mb-10 text-center text-sm text-mist">اختر الخدمة وحيك ونوع سيارتك — وسيُفتح واتساب برسالة جاهزة.</p>
          </Reveal>
          <Reveal><DispatchComposer /></Reveal>
        </div>
      </section>

      {/* SERVICES QUICK */}
      <section className="border-t border-white/5 bg-coal/50 py-16">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <Reveal><h2 className="mb-10 text-center text-2xl font-bold text-white sm:text-3xl">خدمات تصلك إلى موقعك</h2></Reveal>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {quick.map((s, i) => (
              <Reveal key={s.slug} delay={i % 5}>
                <button
                  onClick={() => openWa(pick(s.wa_template_ar, s.wa_template_en))}
                  className="group flex h-full w-full flex-col items-center gap-3 rounded-3xl border border-white/10 bg-ink/70 p-6 text-center transition-all duration-300 hover:-translate-y-1 hover:border-gold/40"
                  data-testid={`lp-service-${s.slug}`}
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-gold/60 text-gold"><ServiceIcon icon={SERVICE_ICON_BY_SLUG[s.slug] || "other"} size={22} /></span>
                  <span className="text-sm font-bold leading-6 text-white group-hover:text-gold">{pick(s.title_ar, s.title_en)}</span>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden border-t border-gold/20 py-20">
        <img src="https://static.prod-images.emergentagent.com/jobs/fda92389-ba5f-436d-8064-e3275d1507fa/images/c61e63e7f7a86e2c47a9ef8f3e568678ce288c16a199374f32f2f2c7b6ef1daf.jpeg" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink via-ink/85 to-ink" />
        <div className="relative mx-auto max-w-2xl px-5 text-center lg:px-8">
          <Reveal>
            <h2 className="text-3xl font-extrabold text-white sm:text-4xl">سيارتك واقفة؟ <span className="gold-text">لا تتحرك — نحن نتحرك إليك</span></h2>
            <p className="mx-auto mt-4 max-w-lg text-mist">راسلنا الآن عبر واتساب أو اتصل مباشرة: 0507359999</p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <button onClick={() => openWa()} className="btn-wa !px-10 !py-4 !text-lg" data-testid="lp-wa-final"><MessageCircle size={20} /> تواصل عبر واتساب</button>
              <a href={PHONE_TEL} className="btn-gold !px-8 !py-4" data-testid="lp-call-final"><Phone size={19} /> اتصال مباشر</a>
            </div>
          </Reveal>
        </div>
      </section>

      <footer className="border-t border-white/10 py-8 text-center">
        <p className="px-5 text-xs leading-6 text-dim">© {new Date().getFullYear()} ميرو للإطارات وخدمات السيارات — خدمة متنقلة في الرياض · <a href="/" className="text-gold">الموقع الكامل</a></p>
      </footer>
    </main>
  );
}
